/**
 * Profil public / pédagogique : titre, présentation, matières, langues, site.
 *
 * Les listes se saisissent en texte séparé par des virgules — un composant à
 * étiquettes viendra avec le design system. Le serveur normalise (blancs
 * rognés, doublons retirés) et **c'est sa réponse qui repeuple le formulaire** :
 * afficher la saisie brute laisserait croire que « Maths, maths » a été
 * enregistré deux fois.
 */
import { useEffect, useState, type SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  useUpdateProfile,
  type Profile,
  type ProfileUpdate,
} from "@/features/account/api/useAccount";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { asDocument, toStored, type ContentDocument } from "@/shared/richtext/document";
import { RichTextEditor } from "@/shared/richtext/RichTextEditor";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";

/**
 * Le budget de la biographie, compté sur le **texte** (`BIO_POLICY` côté
 * serveur). L'éditeur ne le fait pas respecter — un `maxLength` n'a pas de sens
 * sur un document —, il le dit : c'est le serveur qui refuse, et un compteur
 * client qui bloquerait la frappe au caractère près mesurerait le JSON.
 */
const BIO_MAX_LENGTH = 2000;

interface ProfileFormProps {
  profile: Profile;
  canEdit: boolean;
}

/** `"Maths, physique"` → `["Maths", "physique"]`. Le vide donne une liste vide. */
function parseList(raw: string): string[] {
  return raw
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

function sameList(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index]);
}

export function ProfileForm({ profile, canEdit }: ProfileFormProps) {
  const { t } = useTranslation("account");
  const update = useUpdateProfile();
  const errorMessage = useApiErrorMessage();

  const [headline, setHeadline] = useState(profile.headline ?? "");
  const [bio, setBio] = useState<ContentDocument | null>(() => asDocument(profile.bio));
  const [subjects, setSubjects] = useState(profile.subjects.join(", "));
  const [languages, setLanguages] = useState(profile.languages.join(", "));
  const [website, setWebsite] = useState(profile.website_url ?? "");

  useEffect(() => {
    setHeadline(profile.headline ?? "");
    setBio(asDocument(profile.bio));
    setSubjects(profile.subjects.join(", "));
    setLanguages(profile.languages.join(", "));
    setWebsite(profile.website_url ?? "");
  }, [profile]);

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const body: ProfileUpdate = {};
    if (headline !== (profile.headline ?? "")) {
      body.headline = headline.trim() === "" ? null : headline;
    }
    // Comparée **sous sa forme stockée**, des deux côtés : l'état de l'éditeur
    // pour une biographie absente est le document vide, quand le serveur range
    // `null`. Comparer les deux tels quels enverrait `bio: null` à chaque
    // enregistrement d'un profil qui n'en a jamais eu — un effacement que
    // personne n'a demandé, et qui passerait inaperçu puisqu'il n'efface rien.
    const storedBio = toStored(bio);
    if (JSON.stringify(storedBio) !== JSON.stringify(profile.bio ?? null)) {
      body.bio = storedBio;
    }
    const nextSubjects = parseList(subjects);
    if (!sameList(nextSubjects, profile.subjects)) {
      body.subjects = nextSubjects;
    }
    const nextLanguages = parseList(languages);
    if (!sameList(nextLanguages, profile.languages)) {
      body.languages = nextLanguages;
    }
    if (website !== (profile.website_url ?? "")) {
      body.website_url = website.trim() === "" ? null : website;
    }
    if (Object.keys(body).length === 0) {
      return;
    }
    update.mutate(body);
  };

  return (
    <Card title={t("profile.heading")}>
      <form onSubmit={handleSubmit} aria-label={t("profile.heading")} className="space-y-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t("profile.intro")}</p>
        {profile.is_verified && <Alert tone="success">{t("profile.verified")}</Alert>}

        <Field
          label={t("profile.headline")}
          placeholder={t("profile.headlinePlaceholder")}
          value={headline}
          onChange={(event) => {
            setHeadline(event.target.value);
          }}
          disabled={!canEdit}
        />

        <div className="space-y-1.5">
          <RichTextEditor
            label={t("profile.bio")}
            content={bio}
            onChange={setBio}
            disabled={!canEdit}
            hint={t("profile.bioHint", { count: BIO_MAX_LENGTH })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("profile.subjects")}
            hint={t("profile.subjectsHint")}
            value={subjects}
            onChange={(event) => {
              setSubjects(event.target.value);
            }}
            disabled={!canEdit}
          />
          <Field
            label={t("profile.languages")}
            hint={t("profile.subjectsHint")}
            value={languages}
            onChange={(event) => {
              setLanguages(event.target.value);
            }}
            disabled={!canEdit}
          />
        </div>

        <Field
          label={t("profile.website")}
          hint={t("profile.websiteHint")}
          type="url"
          value={website}
          onChange={(event) => {
            setWebsite(event.target.value);
          }}
          disabled={!canEdit}
        />

        {update.isError && <Alert tone="error">{errorMessage(update.error)}</Alert>}
        {update.isSuccess && <Alert tone="success">{t("saved")}</Alert>}

        <Button type="submit" disabled={!canEdit || update.isPending}>
          {update.isPending ? t("saving") : t("save")}
        </Button>
      </form>
    </Card>
  );
}
